import styled from 'styled-components';

export const RiskMatrixContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

export const RiskMatrixWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

export const Cell = styled.div<{ colors?: { bg: string; color: string } }>`
  height: 30px;
  width: 120px;
  border: none;
  padding: 2px;
  display: flex;
  font-size: 12px;
  position: relative;
  align-items: center;
  border-right: 1px solid ${({ theme }) => theme.palette.grey[400]};
  justify-content: ${({ colors }) => (colors ? 'flex-start' : 'center')};
  background-color: ${({ colors }) => (colors ? colors.bg : 'transparent')};
  color: ${({ colors }) => (colors ? colors.color : 'rgba(0, 0, 0, 0.60)')};
`;

export const LikelihoodText = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.common.black};
  transform: translateX(100px) rotate(-90deg);
`;

export const MatrixContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.grey[400]};
  border-right: none;
  border-radius: 5px;
`;

export const ImpactText = styled.p`
  margin: 0;
  font-size: 12px;
  margin-top: 6px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.common.black};
  text-align: center;
`;

export const likelihoods = ['Almost certain', 'Likely', 'Moderate', 'Unlikely', 'Rare'];
export const impacts = ['Insignificant', 'Minor', 'Significant', 'Major', 'Severe'];

export const scores = [
  [5, 10, 15, 20, 25],
  [4, 8, 12, 16, 20],
  [3, 6, 9, 12, 15],
  [2, 4, 6, 8, 10],
  [1, 2, 3, 4, 5],
];

export const getColor = (
  impact: number,
  likelihood: number,
  matrixRows?: number,
  matrixColumns?: number
): { bg: string; color: string } => {
  const score = impact * likelihood;

  // If matrix dimensions are provided, calculate percentage of max score
  if (matrixRows && matrixColumns) {
    const maxScore = matrixRows * matrixColumns;
    const percentage = (score / maxScore) * 100;

    // Define percentage-based thresholds for consistent risk levels across different matrix sizes
    if (percentage <= 8) return { bg: '#37B44A', color: 'white' }; // Very Low (≤8%)
    if (percentage <= 16) return { bg: '#31913A', color: 'white' }; // Low (9-16%)
    if (percentage <= 36) return { bg: '#F9EB11', color: 'black' }; // Medium (17-36%)
    if (percentage <= 64) return { bg: '#FFC554', color: 'black' }; // High (37-64%)
    if (percentage <= 100) return { bg: '#EB1520', color: 'white' }; // Very High (65-100%)
    return { bg: '#BA1417', color: 'white' }; // Extreme (>100% - should not occur)
  }

  // Fallback to original logic for backward compatibility (5x5 matrix)
  // Special case: When impact and likelihood are both 2 (score = 4), override the default color scheme
  // to use green instead of yellow, as this represents a lower risk level despite the score
  if (impact === 2 && likelihood === 2) return { bg: '#31913A', color: 'white' };

  if (score <= 2) return { bg: '#37B44A', color: 'white' };
  if (score < 4) return { bg: '#31913A', color: 'white' };
  if (score <= 9) return { bg: '#F9EB11', color: 'black' };
  if (score <= 12) return { bg: '#FFC554', color: 'black' };
  if (score <= 16) return { bg: '#EB1520', color: 'white' };
  return { bg: '#BA1417', color: 'white' };
};

export const getLabel = (impact: number, likelihood: number, matrixRows?: number, matrixColumns?: number): string => {
  const score = impact * likelihood;

  // If matrix dimensions are provided, calculate percentage of max score
  if (matrixRows && matrixColumns) {
    const maxScore = matrixRows * matrixColumns;
    const percentage = (score / maxScore) * 100;

    // Define percentage-based thresholds for consistent risk levels across different matrix sizes
    if (percentage <= 8) return 'Very low';
    if (percentage <= 16) return 'Low';
    if (percentage <= 36) return 'Medium';
    if (percentage <= 64) return 'High';
    if (percentage <= 100) return 'Very high';
    return 'Extreme';
  }

  // Fallback to original logic for backward compatibility (5x5 matrix)
  // Special case: When impact and likelihood are both 2 (score = 4), override the default label
  // to show 'Low' instead of 'Medium', as this represents a lower risk level despite the score
  if (impact === 2 && likelihood === 2) return 'Low';

  if (score <= 2) return 'Very low';
  if (score < 4) return 'Low';
  if (score <= 9) return 'Medium';
  if (score <= 12) return 'High';
  if (score <= 16) return 'Very high';
  return 'Extreme';
};
